// n=8 élite (PR-N8-4) — Evolución ENTRE informes (web). Consolida §5 del diseño:
//   §5.1 los PILARES del catálogo de esos informes, en orden cronológico.
//   §5.2 un SUBGRÁFICO por pilar con sus conceptos.
// Reusa los extractores de N8-1 (extractPillarSeries / extractCategorySeries) y
// el gráfico de N8-3 (EvaluationEvolutionLines): un ÚNICO motor de evolución
// para toda la web (FichaAlumno + hub EvaluacionesPage).
//
// Gating D-i: con <2 informes muestra el punto único + aviso "disponible a
// partir del 2º informe" y OMITE los subgráficos (anexo). Solo aplica a esta
// evolución ENTRE informes; la intra-periodo (N8-3) nunca se gatea.
//
// Resolución por catalog_version (invariante N8-1): cada serie se resuelve con
// el catálogo de la versión GUARDADA en las filas, no con el vigente. Desde el
// v3 hay dos versiones vivas, así que esto ya no es teoría:
//
//   · Los pilares y los subgráficos salen del catálogo de las filas. Iterar el
//     vigente pedía subgráficos de pilares que el histórico no tiene, y salían
//     vacíos sin decir nada.
//   · Una serie que CRUZA versiones no se dibuja: `bloqueo`, con el motivo.
//     Un v2 y un v3 en la misma línea tendrían una pendiente que no significa
//     nada, porque técnico mide otra cosa en cada uno.

import {
  extractPillarSeries,
  extractCategorySeries,
  textoBloqueo,
  canShowEvolution,
  type EvalRow,
} from '../lib/evaluationSeries';
import EvaluationEvolutionLines from './EvaluationEvolutionLines';

interface Props {
  evals: EvalRow[]; // filas crudas: period_start, report_number?, detailed_scores
  // §5.2 — FichaAlumno (informe completo) = true; el hub EvaluacionesPage = false
  // (solo los pilares, para no recargar la lista de browse). Default: true.
  showSubcharts?: boolean;
}

export default function EvaluationEvolutionBetween({ evals, showSubcharts = true }: Props) {
  const count = evals.length;
  if (count === 0) return null;

  const full = canShowEvolution(count); // ≥2 informes
  const pillarData = extractPillarSeries(evals);

  return (
    <div className="space-y-4" data-testid="evolution-between" data-reports={count}>
      {/* §5.1 — los 4 pilares (técnico/táctico/físico/actitud), escala 1-10. */}
      <div className="card" data-testid="evolution-pillars">
        <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-2">
          Evolución entre informes
        </h3>
        {!full && (
          <p className="text-text-secondary text-xs mb-2" data-testid="evolution-gating">
            Disponible a partir del 2º informe.
          </p>
        )}
        {/* Un gráfico vacío se lee como «no hay datos». Aquí sí hay, y por eso
            no se dibujan: no son comparables. */}
        {pillarData.bloqueo ? (
          <p className="text-text-secondary text-xs" data-testid="evolution-bloqueo">
            {textoBloqueo(pillarData.bloqueo)}
          </p>
        ) : (
          <EvaluationEvolutionLines
            points={pillarData.periods.map((p) => ({ label: p.label }))}
            series={pillarData.series.map((s) => ({
              key: s.key,
              label: s.label,
              color: s.color,
              values: s.points,
            }))}
            yMin={1}
          />
        )}
      </div>

      {/* §5.2 — un subgráfico por pilar con sus categorías. Anexo: solo ≥2 (D-i). */}
      {showSubcharts && full && !pillarData.bloqueo && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="evolution-subcharts">
          {/* LOS PILARES DE LA SERIE, NO LOS DEL CATÁLOGO VIGENTE.
              Iteraba `CATALOG`, y con dos versiones vivas eso pide subgráficos
              de pilares que el catálogo del histórico no tiene: salen vacíos y
              sin decir nada. `pillarData.series` ya viene resuelto de la
              versión de las filas. */}
          {pillarData.series.map((p) => {
            const data = extractCategorySeries(evals, p.key);
            return (
              <div key={p.key} className="card" data-testid={`evolution-subchart-${p.key}`}>
                <h4 className="font-oswald text-sm mb-2" style={{ color: p.color }}>
                  {p.label}
                </h4>
                <EvaluationEvolutionLines
                  points={data.periods.map((pt) => ({ label: pt.label }))}
                  series={data.series.map((s) => ({
                    key: s.key,
                    label: s.label,
                    color: s.color,
                    values: s.points,
                  }))}
                  height={200}
                  yMin={1}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
